import { useCallback, useEffect, useState } from 'react'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'
import { useParams } from 'react-router-dom'

import { Button, Input, Typography, Select, Flex, Tooltip } from 'antd'
import _ from 'lodash'
import * as Icons from '@ant-design/icons'
import styled from 'styled-components'
import { colours } from '@qflow/theme'

import * as Rules from 'admin/lib/ValidationRules'
import { usDateToFormDate } from 'admin/lib/dates'
import { Translate } from 'admin/lib/Icons/Translate'

import {
  AutoCompleteFormItem,
  FormItem,
  FormSpace,
  FormSpaceSmall,
  DateTimeUS,
  FormRowWithValidation,
  StyledSection
} from 'admin/lib/Forms'
import { Undo } from 'admin/lib/Icons/Undo'

import { HandleSingleAndDoubleClick } from 'admin/lib/Functions/handleSingleAndDoubleClick'
import { usDateFormatter } from 'admin/lib/Functions/timeZoneHelper'
import {
  ConvertToMetersObject,
  DeliveryProductCertOptions,
  IMPERIAL_VOLUME_UNITS,
  IMPERIAL_WEIGHT_UNITS,
  METRIC_VOLUME_UNITS,
  METRIC_WEIGHT_UNITS,
  MeasurementUoM
} from 'admin/lib/Records'
import {
  Delivery as fields,
  DeliveryImperial as fieldsImperial
} from 'admin/lib/Records/fields'
import {
  CheckHeightLengthWidth,
  CheckProductName,
  CheckQuantity,
  CheckVolume,
  CheckWeight
} from 'admin/lib/SimpleDataValidation'
import {
  AddMultipleLineItemsModal,
  UnitSelector,
  useLineItemShortcuts
} from 'admin/pages/RecordEdit/common'

import { useLineItemStore } from 'admin/pages/RecordEdit/LineItemStore'
import {
  returnDefaultValueForLineItemField,
  returnValueForLineItemField
} from 'admin/pages/RecordEdit/common/returnValueForLineItemField'

import { ReferenceProductItemSelect } from 'admin/pages/RecordEdit/Delivery/ReferenceProductItemSelect'
import {
  calculateVolume,
  calculateImperialVolume
} from 'admin/pages/RecordEdit/Delivery/calculateVolume'
import { estimateRebarWeight } from 'admin/pages/RecordEdit/Delivery/estimateRebarWeight'
import {
  RAW,
  MAX_MEASUREMENT_SIZES
} from 'admin/pages/RecordEdit/common/constants'
import {
  isDisabled,
  isExtendedPGList,
  isHidden
} from 'admin/pages/RecordEdit/common/rawEnrichedHelpers'
import { LineItemTitle } from 'admin/pages/RecordEdit/common/styledComponents'

export function DeliveryLineItem({
  trigger,
  index,
  item,
  rawItem,
  onDuplicate,
  onDuplicateMultiple,
  onRemove,
  isImperial,
  dataView,
  showRaw,
  setEnrichedDisabled
}) {
  const [unsavedChanges, setUnsavedChanges] = useState(false)
  const [productDetailsOpen, setProductDetailsOpen] = useState(true)
  const [measurementsOpen, setMeasurementsOpen] = useState(true)
  const [concreteDatesOpen, setConcreteDatesOpen] = useState(
    !!item.productionTime
  )
  const [isDuplicateItemsMultipleOpen, setIsDuplicateItemsMultipleOpen] =
    useState(false)

  const [producedAtDateWarningMessage, setProducedAtDateWarningMessage] =
    useState('')
  const [showProducedAtDateWarning, setShowProducedAtDateWarning] =
    useState(false)

  const [usedAtDateWarningMessage, setUsedAtDateWarningMessage] = useState('')
  const [showUsedAtDateWarning, setShowUsedAtDateWarning] = useState(false)

  const lineItem = useLineItemStore(state => state[index])
  const deleteLineItem = useLineItemStore(state => state.deleteKey)
  const duplicateLineItem = useLineItemStore(state => state.duplicateKey)
  const { moduleName, recordId } = useParams()

  const namePrefix = `uniquePrefix[${index}].`
  const rawNamePrefix = `uniquePrefix[${index}].raw_`

  // Alias Used for autocomplete fields
  const aliasPrefix = `enriched.items[${index}].`
  const rawAliasPrefix = `raw.items[${index}].`

  const REBAR_LENGTH_WARNING_MESSAGE =
    'The value entered for Length seems big, have you set the correct unit?'

  const REBAR_WIDTH_WARNING_MESSAGE =
    'The value entered for Width seems big, have you set the correct unit?'

  const PRODUCED_AT_DATE_AFTER_ARRIVAL_DATE_WARNING_MESSAGE =
    'Entered Produced At date is after the Arrival Date for this record'

  const PRODUCED_AT_DATE_AFTER_EXIT_DATE_WARNING_MESSAGE =
    'Entered Produced At date is after the Exit Date for this record'

  const USED_AT_DATE_BEFORE_PRODUCED_AT_DATE_WARNING_MESSAGE =
    'Entered Used At date is before the Produced At Date for this record'

  const USED_AT_DATE_AFTER_EXIT_DATE_WARNING_MESSAGE =
    'Entered Used At date is after the Exit Date for this record'

  const form = useForm({
    reValidateMode: 'onChange',
    mode: 'all'
  })

  const { getValues } = useFormContext()
  const deliveryArrivalDate = getValues().enriched?.deliveryArrivalDate
  const deliveryExitDate = getValues().enriched?.deliveryExitDate

  const [name, setName] = useState(item.name)

  const { TextArea } = Input

  const dataChange = event => {
    setUnsavedChanges(true)

    // Handle multiple events (e.g. for select fields that update other fields)
    if (Array.isArray(event)) {
      const newLineItem = { ...lineItem }
      event.forEach(eventPart => {
        const lineItemKey = eventPart.target.name.split(namePrefix).pop()
        newLineItem[lineItemKey] = eventPart.target.value

        if (
          lineItemKey.startsWith('raw_') &&
          lineItem[lineItemKey] !== eventPart.target.value
        ) {
          setEnrichedDisabled(true)
        }
      })

      useLineItemStore.setState({ [index]: newLineItem })
    } else {
      const lineItemKey = event.target.name.split(namePrefix).pop()

      if (
        lineItemKey.startsWith('raw_') &&
        lineItem[lineItemKey] !== event.target.value
      ) {
        setEnrichedDisabled(true)
      }

      if (lineItemKey === 'name') {
        setName(event.target.value)
      }

      const newLineItem = {
        ...lineItem,
        [lineItemKey]:
          event.target.type === 'number'
            ? parseFloat(event.target.value)
            : event.target.value
      }

      if (event.target.type === 'number' && isNaN(newLineItem[lineItemKey])) {
        delete newLineItem[lineItemKey]
      }

      useLineItemStore.setState({ [index]: newLineItem })

      // If UoM change also trigger a blur event for the parent input
      // so that we trigger any validation rules
      if (
        lineItemKey.includes('UoM') &&
        lineItem[lineItemKey] !== event.target.value
      ) {
        const selector = `[name='${event.target.name.split('UoM')[0]}']`
        setTimeout(() => {
          document.querySelector(selector)?.blur()
          document.querySelector(selector)?.focus()
        }, 1)
      }

      // If productGroups is changed also trigger a blur event for the lineItem's givenVolume and givenWeight
      if (
        lineItemKey === 'productGroups' &&
        lineItem[lineItemKey] !== event.target.value
      ) {
        const volumeSelector = `[name='${namePrefix}${fields.lineItems.givenVolume.path}']`
        const weightSelector = `[name='${namePrefix}${fields.lineItems.weight.path}']`
        setTimeout(() => {
          const currentlyFocusedElement = document.activeElement
          document.querySelector(volumeSelector)?.focus()
          document.querySelector(volumeSelector)?.blur()
          document.querySelector(weightSelector)?.focus()
          document.querySelector(weightSelector)?.blur()
          // Refocus the element that was focused before
          currentlyFocusedElement.focus()
        }, 1)
      }
    }
  }

  const handleFocus = event => {
    const clipboardValue = sessionStorage.getItem('clipboard')

    if (clipboardValue && clipboardValue !== '') {
      const updatedValue = returnValueForLineItemField(
        clipboardValue,
        event.target.type,
        event.target.value
      )

      if (event.target.name.startsWith('uniquePrefix')) {
        // All 'real' fields starts with 'uniquePrefix'
        // so we'll only handle those first

        form.setValue(event.target.name, updatedValue)
        // Clear 'clipboard' value
        sessionStorage.removeItem('clipboard')

        const updatedEvent = { ...event }
        updatedEvent.target.value = updatedValue
        dataChange(updatedEvent)
      }
    }
  }

  const handleOnSelect = (value, fieldId) => {
    // Previously was an array of multiple values,
    // now this will always be a single value but we need to keep the array for legacy records
    const mockEvent = {
      target: {
        name: fieldId,
        value: value === null ? [] : [value]
      }
    }
    dataChange(mockEvent)
  }

  const openDuplicateItemMultipleModal = e => {
    setIsDuplicateItemsMultipleOpen(true)
  }

  const closeDuplicateItemMultipleModal = e => {
    setIsDuplicateItemsMultipleOpen(false)
  }

  const duplicate = event => {
    // Duplicate on state
    duplicateLineItem(index)
    // Duplicate on Parent
    onDuplicate(lineItem)
  }

  const duplicateMultiple = numOfNewItems => {
    if (numOfNewItems > 0) {
      for (let i = 1; i <= numOfNewItems; i++) {
        // Duplicate on state
        duplicateLineItem(index)
      }
      onDuplicateMultiple(numOfNewItems, lineItem)
    }
  }

  const handleDuplicate = HandleSingleAndDoubleClick(
    duplicate,
    openDuplicateItemMultipleModal
  )

  const handleRemove = event => {
    // if lineitem has id (which means it has been saved) just change the isDeleted flag
    // Otherwise remove the entire item
    if (lineItem.id) {
      // Create events for Raw and Data and pass to dataChange
      const mockEvent = {
        target: {
          name: namePrefix + 'isDeleted',
          value: true
        }
      }
      const secondMockEvent = {
        target: {
          name: rawNamePrefix + 'isDeleted',
          value: true
        }
      }
      dataChange([mockEvent, secondMockEvent])
    } else {
      // Remove from parent
      onRemove()
      // Remove from state
      deleteLineItem(index)
    }
  }

  const handleUndoRemove = event => {
    // Only shown to deleted products that have a real id
    if (lineItem.id) {
      // Create events for Raw and Data and pass to dataChange
      const mockEvent = {
        target: {
          name: namePrefix + 'isDeleted',
          value: false
        }
      }
      const secondMockEvent = {
        target: {
          name: rawNamePrefix + 'isDeleted',
          value: false
        }
      }
      dataChange([mockEvent, secondMockEvent])
    }
  }

  const handleWheelScrollInNumberField = event => {
    if (
      document.activeElement.type === 'number' &&
      document.activeElement.matches(':hover')
    ) {
      event.preventDefault()
    }
  }

  const shortcuts = useLineItemShortcuts(handleRemove, duplicate)

  useEffect(() => {
    // If line Item has been changed but not saved and the 'Add New Line Item' button is pressed
    // Stop entered values from being overwritten with original values
    if (item && rawItem && !unsavedChanges) {
      // Populate from record
      const rawAndEnhancedItem = { ...item }
      Object.entries(rawItem).forEach(entry => {
        const [key, value] = entry
        rawAndEnhancedItem[`raw_${key}`] = value
      })
      useLineItemStore.setState({ [index]: rawAndEnhancedItem })
    }
  }, [index, item, rawItem, unsavedChanges])

  useEffect(() => {
    document.addEventListener('wheel', handleWheelScrollInNumberField, {
      passive: false
    })
  }, [])

  const HasWeirdRebarLength = () => {
    const lengthUoM = lineItem.lengthUoM ? lineItem.lengthUoM : 'Millimeter'

    return (
      lineItem.name &&
      lineItem.name.toLowerCase().includes('rebar') &&
      lineItem.length * ConvertToMetersObject[lengthUoM] >
        MAX_MEASUREMENT_SIZES.rebarLength
    )
  }

  const HasWeirdRebarWidth = () => {
    const widthUoM = lineItem.widthUoM ? lineItem.widthUoM : 'Millimeter'

    return (
      lineItem.name &&
      lineItem.name.toLowerCase().includes('rebar') &&
      lineItem.width * ConvertToMetersObject[widthUoM] >
        MAX_MEASUREMENT_SIZES.rebarWidth
    )
  }

  const handleVolumeCalculation = () => {
    const { calculatedVolume } = lineItem
    let volume
    if (!isImperial) {
      const {
        height,
        length,
        width,
        quantity,
        heightUoM,
        lengthUoM,
        widthUoM
      } = lineItem
      volume = calculateVolume({
        height,
        heightUoM,
        length,
        lengthUoM,
        width,
        widthUoM,
        quantity
      })
    } else if (isImperial) {
      const {
        heightYards,
        heightFeet,
        heightInches,
        lengthYards,
        lengthFeet,
        lengthInches,
        widthYards,
        widthFeet,
        widthInches,
        quantity
      } = lineItem
      volume = calculateImperialVolume({
        heightYards,
        heightFeet,
        heightInches,
        lengthYards,
        lengthFeet,
        lengthInches,
        widthYards,
        widthFeet,
        widthInches,
        quantity
      })
    }

    if (volume !== null && volume !== calculatedVolume) {
      useLineItemStore.setState({
        [index]: { ...lineItem, calculatedVolume: volume }
      })
    }

    handleWeightCalculation()
  }

  const handleWeightCalculation = event => {
    const {
      name,
      length,
      lengthUoM,
      width,
      widthUoM,
      quantity,
      estimatedWeight
    } = lineItem
    if (!name || !name.toLowerCase().includes('rebar')) {
      if (event) {
        handleFocus(event)
      }
    } else {
      const weight = estimateRebarWeight({
        diameter: width,
        diameterUoM: widthUoM,
        length,
        lengthUoM: lengthUoM,
        quantity
      })

      if (weight !== null && weight !== estimatedWeight) {
        useLineItemStore.setState({
          [index]: { ...lineItem, estimatedWeight: weight }
        })
      }
    }
  }

  const validateProducedAtDate = useCallback(() => {
    if (
      lineItem &&
      lineItem.productionTime &&
      deliveryArrivalDate &&
      new Date(lineItem.productionTime) > new Date(deliveryArrivalDate)
    ) {
      setProducedAtDateWarningMessage(
        PRODUCED_AT_DATE_AFTER_ARRIVAL_DATE_WARNING_MESSAGE
      )
      setShowProducedAtDateWarning(true)
    } else if (
      lineItem &&
      lineItem.productionTime &&
      deliveryExitDate &&
      new Date(lineItem.productionTime) > new Date(deliveryExitDate)
    ) {
      setProducedAtDateWarningMessage(
        PRODUCED_AT_DATE_AFTER_EXIT_DATE_WARNING_MESSAGE
      )
      setShowProducedAtDateWarning(true)
    } else {
      setProducedAtDateWarningMessage('')
      setShowProducedAtDateWarning(false)
    }
    // Always return TRUE so we do not block form submission
    return true
  }, [deliveryArrivalDate, deliveryExitDate, lineItem])

  const validateUsedAtDate = useCallback(() => {
    if (
      lineItem &&
      lineItem.usedAtTime &&
      lineItem.productionTime &&
      new Date(lineItem.usedAtTime) < new Date(lineItem.productionTime)
    ) {
      setUsedAtDateWarningMessage(
        USED_AT_DATE_BEFORE_PRODUCED_AT_DATE_WARNING_MESSAGE
      )
      setShowUsedAtDateWarning(true)
    } else if (
      lineItem &&
      lineItem.usedAtTime &&
      deliveryExitDate &&
      new Date(lineItem.usedAtTime) > new Date(deliveryExitDate)
    ) {
      setUsedAtDateWarningMessage(USED_AT_DATE_AFTER_EXIT_DATE_WARNING_MESSAGE)
      setShowUsedAtDateWarning(true)
    } else {
      setUsedAtDateWarningMessage('')
      setShowUsedAtDateWarning(false)
    }
    // Always return TRUE so we do not block form submission
    return true
  }, [deliveryExitDate, lineItem])

  useEffect(() => {
    if (lineItem) {
      validateUsedAtDate()
      validateProducedAtDate()
    }
  }, [lineItem, validateProducedAtDate, validateUsedAtDate])

  const handleUsedAtTimeFocus = event => {
    // If OCR value has been clicked we trigger the main handleFocus function
    // Else if usedAtTime is empty copy value from production time
    const clipboardValue = sessionStorage.getItem('clipboard')
    if (clipboardValue && clipboardValue !== '') {
      handleFocus(event)
    } else if (event.target.value === '') {
      useLineItemStore.setState({
        [index]: { ...lineItem, usedAtTime: lineItem.productionTime }
      })
    }
  }

  const handleUsProductionTimeBlur = value => {
    const mockEvent = {
      target: {
        name: namePrefix + fields.lineItems.productionTime.path,
        value: usDateToFormDate(value)
      }
    }

    dataChange(mockEvent)
  }

  const handleUsUsedAtTimeBlur = value => {
    const mockEvent = {
      target: {
        name: namePrefix + fields.lineItems.usedAtTime.path,
        value: usDateToFormDate(value)
      }
    }

    dataChange(mockEvent)
  }

  const handleProductOpenChange = () => {
    setProductDetailsOpen(!productDetailsOpen)
    sessionStorage.setItem(`${recordId}-${index}-open`, !productDetailsOpen)
  }

  useEffect(() => {
    const open = sessionStorage.getItem(`${recordId}-${index}-open`)
    if (open) {
      setProductDetailsOpen(open === 'true')
    }
    // check for changes to trigger value by adding to dependency array
  }, [index, recordId, trigger])

  if (!lineItem) {
    return null
  }

  if (lineItem.isDeleted) {
    return (
      <FormProvider {...form}>
        <StyledSection
          className={'LineItem'}
          onKeyDown={shortcuts.onKeyDown}
          colour="alt"
        >
          {({ firstInputRef }) => (
            <LineItemTitle>
              <Typography.Text
                strong
                type="secondary"
                ellipsis={true}
                style={{
                  width: '272px',
                  paddingLeft: '1rem',
                  textIndent: '-1rem'
                }}
              >
                <Typography.Text strong>{index + 1}:</Typography.Text>{' '}
                <span style={{ textDecoration: 'line-through' }}>
                  {name || 'New Product'}
                </span>
                <br />
                Deleted
              </Typography.Text>

              <Button
                type="text"
                tabIndex={-1}
                icon={<Undo />}
                onClick={handleUndoRemove}
                title="Undo delete"
              />
            </LineItemTitle>
          )}
        </StyledSection>
      </FormProvider>
    )
  }

  if (lineItem.isRemoved) {
    return (
      <FormProvider {...form}>
        <StyledSection
          className={'LineItem'}
          onKeyDown={shortcuts.onKeyDown}
          colour="alt"
        >
          {({ firstInputRef }) => (
            <LineItemTitle>
              <Typography.Text
                strong
                type="secondary"
                ellipsis={true}
                style={{
                  width: '272px',
                  paddingLeft: '1rem',
                  textIndent: '-1rem'
                }}
              >
                <Typography.Text strong>{index + 1}:</Typography.Text>{' '}
                <span style={{ textDecoration: 'line-through' }}>
                  {name || 'New Product'}
                </span>
                <br />
                Removed by client
              </Typography.Text>
            </LineItemTitle>
          )}
        </StyledSection>
      </FormProvider>
    )
  }

  return (
    <FormProvider {...form}>
      <StyledSection
        className={'LineItem'}
        onKeyDown={shortcuts.onKeyDown}
        colour="alt"
      >
        {({ firstInputRef }) => (
          <details
            {...(productDetailsOpen ? { open: true } : {})}
            className="LineItemDetails"
            style={{ position: 'relative' }}
          >
            <LineItemTitle
              onClick={e => {
                // prevent default on the Click Event to stop it conflicting
                e.preventDefault()
              }}
            >
              <Typography.Text
                strong
                type="secondary"
                ellipsis={true}
                style={{ width: '272px' }}
                onClick={handleProductOpenChange}
              >
                <Typography.Text strong>{index + 1}:</Typography.Text>{' '}
                {name || 'New Product'}
              </Typography.Text>

              <ButtonGroup>
                {dataView === RAW && (
                  <>
                    <Button
                      type="text"
                      tabIndex={-1}
                      icon={<Icons.CopyOutlined />}
                      onClick={handleDuplicate}
                      title="Duplicate"
                    />
                    <Button
                      type="text"
                      tabIndex={-1}
                      danger
                      icon={<Icons.DeleteOutlined />}
                      onClick={handleRemove}
                      title="Delete"
                    />
                  </>
                )}

                {productDetailsOpen && (
                  <UpCircleFilledInline
                    title="Click to collapse"
                    className="productCollapseIcon"
                    onClick={handleProductOpenChange}
                  />
                )}
                {!productDetailsOpen && (
                  <DownCircleOutlinedInline
                    title="Click to expand"
                    onClick={handleProductOpenChange}
                  />
                )}
              </ButtonGroup>
            </LineItemTitle>
            <FormItem
              name={rawNamePrefix + fields.rawLineItems.id.path}
              defaultValue={returnDefaultValueForLineItemField(
                rawItem.id,
                lineItem['raw_id']
              )}
              style={{ display: 'none' }}
            >
              <Input type="hidden" />
            </FormItem>
            <FormItem
              name={namePrefix + fields.lineItems.id.path}
              defaultValue={returnDefaultValueForLineItemField(
                item.id,
                lineItem.id
              )}
              style={{ display: 'none' }}
            >
              <Input type="hidden" />
            </FormItem>

            <FormItem
              label={fields.rawLineItems.name.label}
              name={rawNamePrefix + fields.rawLineItems.name.path}
              alias={rawAliasPrefix + fields.rawLineItems.name.path}
              inputRef={dataView === RAW ? firstInputRef : null}
              defaultValue={returnDefaultValueForLineItemField(
                rawItem.name,
                lineItem['raw_name']
              )}
              onBlur={dataChange}
              onChange={dataChange}
              onFocus={handleFocus}
              children={<TextArea autoSize />}
              disabled={isDisabled(true, dataView, showRaw)}
              hidden={isHidden(true, dataView, showRaw)}
              instrumented={true}
            />

            <Flex align="center" gap="4px">
              <AutoCompleteFormItem
                label={fields.lineItems.name.label}
                name={namePrefix + fields.lineItems.name.path}
                alias={aliasPrefix + fields.lineItems.name.path}
                inputRef={dataView !== RAW ? firstInputRef : null}
                defaultValue={returnDefaultValueForLineItemField(
                  item.name,
                  lineItem.name
                )}
                rules={Rules.MandatoryString}
                onBlur={dataChange}
                onChange={dataChange}
                onFocus={handleFocus}
                id={`deliveryLineItemName_${index}`}
                simpleDataValidationRules={CheckProductName}
                children={<TextArea autoSize />}
                disabled={isDisabled(
                  fields.lineItems.name.isRawField,
                  dataView,
                  showRaw
                )}
                hidden={isHidden(
                  fields.lineItems.name.isRawField,
                  dataView,
                  showRaw
                )}
                style={{ flexGrow: 1, width: '-webkit-fill-available' }}
              />
              {dataView !== RAW && (
                <Tooltip title="Translate to english">
                  <div
                    style={{ width: '20px', cursor: 'pointer' }}
                    onClick={() => {
                      window.open(
                        `https://www.google.com/search?q=Translate+${name}+from+any+language+to+English`,
                        '_blank'
                      )
                    }}
                  >
                    <Translate />
                  </div>
                </Tooltip>
              )}
            </Flex>
            <FormSpace />

            <FormItem
              label={fields.rawLineItems.quantity.label}
              name={rawNamePrefix + fields.rawLineItems.quantity.path}
              defaultValue={returnDefaultValueForLineItemField(
                rawItem.quantity,
                lineItem['raw_quantity']
              )}
              onChange={dataChange}
              onFocus={handleFocus}
              disabled={isDisabled(true, dataView, showRaw)}
              hidden={isHidden(true, dataView, showRaw)}
              instrumented={true}
            />
            <FormItem
              label={fields.lineItems.quantity.label}
              name={namePrefix + fields.lineItems.quantity.path}
              defaultValue={
                // Additional check because quantity defaults to 1
                item.quantity === 1 && lineItem.quantity
                  ? lineItem.quantity
                  : item.quantity
                    ? item.quantity
                    : lineItem.quantity
              }
              rules={Rules.NonNegativeNumber}
              onChange={dataChange}
              onFocus={handleFocus}
              onBlur={handleVolumeCalculation}
              id={`deliveryLineItemQuantity_${index}`}
              simpleDataValidationRules={v =>
                CheckQuantity(typeof v === 'number' ? v.toString() : v)
              }
              disabled={isDisabled(
                fields.lineItems.quantity.isRawField,
                dataView,
                showRaw
              )}
              hidden={isHidden(
                fields.lineItems.quantity.isRawField,
                dataView,
                showRaw
              )}
              instrumented={true}
            >
              <Input type="number" min="0" step="any" />
            </FormItem>

            <FormSpace />
            <hr />
            <details
              onClick={e => {
                // prevent default on the Click Event to stop it conflicting
                e.preventDefault()
              }}
              {...(measurementsOpen ? { open: true } : {})}
              style={{ position: 'relative' }}
            >
              <summary
                onClick={() => {
                  setMeasurementsOpen(prev => !prev)
                }}
                style={{ width: 'fit-content' }}
              >
                <span>Measurements</span>
                {measurementsOpen && (
                  <UpCircleFilled title="Click to collapse" />
                )}
                {!measurementsOpen && (
                  <DownCircleOutlined title="Click to expand" />
                )}
              </summary>

              <FormRowWithValidation>
                <div>
                  <FormItem
                    label={fields.rawLineItems.height.label}
                    name={rawNamePrefix + fields.rawLineItems.height.path}
                    defaultValue={returnDefaultValueForLineItemField(
                      rawItem.height,
                      lineItem['raw_height']
                    )}
                    onChange={dataChange}
                    onFocus={handleFocus}
                    disabled={isDisabled(true, dataView, showRaw)}
                    hidden={isHidden(true, dataView, showRaw)}
                    instrumented={true}
                  />
                </div>
                <div>
                  <FormItem
                    label={fields.rawLineItems.width.label}
                    name={rawNamePrefix + fields.rawLineItems.width.path}
                    defaultValue={returnDefaultValueForLineItemField(
                      rawItem.width,
                      lineItem['raw_width']
                    )}
                    onChange={dataChange}
                    onFocus={handleFocus}
                    disabled={isDisabled(true, dataView, showRaw)}
                    hidden={isHidden(true, dataView, showRaw)}
                    instrumented={true}
                  />
                </div>
                <div>
                  <FormItem
                    label={fields.rawLineItems.length.label}
                    name={rawNamePrefix + fields.rawLineItems.length.path}
                    defaultValue={returnDefaultValueForLineItemField(
                      rawItem.length,
                      lineItem['raw_length']
                    )}
                    onChange={dataChange}
                    onFocus={handleFocus}
                    disabled={isDisabled(true, dataView, showRaw)}
                    hidden={isHidden(true, dataView, showRaw)}
                    instrumented={true}
                  />
                </div>
              </FormRowWithValidation>

              {!isImperial && (
                <FormRowWithValidation>
                  <div>
                    <FormItem
                      label={fields.lineItems.height.label}
                      name={namePrefix + fields.lineItems.height.path}
                      defaultValue={returnDefaultValueForLineItemField(
                        item.height,
                        lineItem.height
                      )}
                      rules={Rules.NonNegativeNumber}
                      onChange={dataChange}
                      onFocus={handleFocus}
                      onBlur={handleVolumeCalculation}
                      id={`deliveryLineItemHeight_${index}`}
                      simpleDataValidationRules={v =>
                        CheckHeightLengthWidth(
                          v,
                          MAX_MEASUREMENT_SIZES.height,
                          lineItem.heightUoM
                        )
                      }
                      disabled={isDisabled(
                        fields.lineItems.height.isRawField,
                        dataView,
                        showRaw
                      )}
                      hidden={isHidden(
                        fields.lineItems.height.isRawField,
                        dataView,
                        showRaw
                      )}
                      instrumented={true}
                    >
                      <Input
                        type="number"
                        min="0"
                        step="any"
                        suffix={
                          <UnitSelector
                            name={namePrefix + fields.lineItems.heightUoM.path}
                            currentValue={
                              lineItem.heightUoM
                                ? lineItem.heightUoM
                                : 'Millimeter'
                            }
                            allValues={MeasurementUoM}
                            onChange={dataChange}
                            disabled={isDisabled(
                              fields.lineItems.height.isRawField,
                              dataView,
                              showRaw
                            )}
                          />
                        }
                      />
                    </FormItem>
                  </div>
                  <div
                    className={
                      HasWeirdRebarWidth() ? 'contains-validation-warning' : ''
                    }
                  >
                    <FormItem
                      label={fields.lineItems.width.label}
                      name={namePrefix + fields.lineItems.width.path}
                      defaultValue={returnDefaultValueForLineItemField(
                        item.width,
                        lineItem.width
                      )}
                      rules={Rules.NonNegativeNumber}
                      onChange={dataChange}
                      onFocus={handleFocus}
                      onBlur={handleVolumeCalculation}
                      id={`deliveryLineItemWidth_${index}`}
                      simpleDataValidationRules={v =>
                        CheckHeightLengthWidth(
                          v,
                          MAX_MEASUREMENT_SIZES.width,
                          lineItem.widthUoM
                        )
                      }
                      disabled={isDisabled(
                        fields.lineItems.width.isRawField,
                        dataView,
                        showRaw
                      )}
                      hidden={isHidden(
                        fields.lineItems.width.isRawField,
                        dataView,
                        showRaw
                      )}
                      instrumented={true}
                    >
                      <Input
                        type="number"
                        min="0"
                        step="any"
                        suffix={
                          <UnitSelector
                            name={namePrefix + fields.lineItems.widthUoM.path}
                            currentValue={
                              lineItem.widthUoM
                                ? lineItem.widthUoM
                                : 'Millimeter'
                            }
                            allValues={MeasurementUoM}
                            onChange={dataChange}
                            disabled={isDisabled(
                              fields.lineItems.width.isRawField,
                              dataView,
                              showRaw
                            )}
                          />
                        }
                      />
                    </FormItem>
                  </div>
                  <div
                    className={
                      HasWeirdRebarLength() ? 'contains-validation-warning' : ''
                    }
                  >
                    <FormItem
                      label={fields.lineItems.length.label}
                      name={namePrefix + fields.lineItems.length.path}
                      defaultValue={returnDefaultValueForLineItemField(
                        item.length,
                        lineItem.length
                      )}
                      rules={Rules.NonNegativeNumber}
                      onChange={dataChange}
                      onFocus={handleFocus}
                      onBlur={handleVolumeCalculation}
                      id={`deliveryLineItemLength_${index}`}
                      simpleDataValidationRules={v =>
                        CheckHeightLengthWidth(
                          v,
                          MAX_MEASUREMENT_SIZES.length,
                          lineItem.lengthUoM
                        )
                      }
                      disabled={isDisabled(
                        fields.lineItems.length.isRawField,
                        dataView,
                        showRaw
                      )}
                      hidden={isHidden(
                        fields.lineItems.length.isRawField,
                        dataView,
                        showRaw
                      )}
                      instrumented={true}
                    >
                      <Input
                        type="number"
                        min="0"
                        step="any"
                        suffix={
                          <UnitSelector
                            name={namePrefix + fields.lineItems.lengthUoM.path}
                            currentValue={
                              lineItem.lengthUoM
                                ? lineItem.lengthUoM
                                : 'Millimeter'
                            }
                            allValues={MeasurementUoM}
                            onChange={dataChange}
                            disabled={isDisabled(
                              fields.lineItems.length.isRawField,
                              dataView,
                              showRaw
                            )}
                          />
                        }
                      />
                    </FormItem>
                  </div>
                </FormRowWithValidation>
              )}

              {isImperial && (
                <>
                  <FormRowWithValidation>
                    <FormItem
                      label="Height"
                      name={
                        namePrefix + fieldsImperial.lineItems.heightYards.path
                      }
                      defaultValue={returnDefaultValueForLineItemField(
                        item.heightYards,
                        lineItem.heightYards
                      )}
                      rules={Rules.NonNegativeNumber}
                      onChange={dataChange}
                      onFocus={handleFocus}
                      onBlur={handleVolumeCalculation}
                      disabled={isDisabled(
                        fieldsImperial.lineItems.heightYards.isRawField,
                        dataView,
                        showRaw
                      )}
                      hidden={isHidden(
                        fieldsImperial.lineItems.heightYards.isRawField,
                        dataView,
                        showRaw
                      )}
                      instrumented={true}
                    >
                      <Input
                        type="number"
                        min="0"
                        step="any"
                        placeholder={fieldsImperial.lineItems.heightYards.label}
                        suffix="yd"
                      />
                    </FormItem>

                    <FormItem
                      label="Width"
                      name={
                        namePrefix + fieldsImperial.lineItems.widthYards.path
                      }
                      defaultValue={returnDefaultValueForLineItemField(
                        item.widthYards,
                        lineItem.widthYards
                      )}
                      rules={Rules.NonNegativeNumber}
                      onChange={dataChange}
                      onFocus={handleFocus}
                      onBlur={handleVolumeCalculation}
                      disabled={isDisabled(
                        fieldsImperial.lineItems.widthYards.isRawField,
                        dataView,
                        showRaw
                      )}
                      hidden={isHidden(
                        fieldsImperial.lineItems.widthYards.isRawField,
                        dataView,
                        showRaw
                      )}
                      instrumented={true}
                    >
                      <Input
                        type="number"
                        min="0"
                        step="any"
                        placeholder={fieldsImperial.lineItems.widthYards.label}
                        suffix="yd"
                      />
                    </FormItem>

                    <FormItem
                      label="Length"
                      name={
                        namePrefix + fieldsImperial.lineItems.lengthYards.path
                      }
                      defaultValue={returnDefaultValueForLineItemField(
                        item.lengthYards,
                        lineItem.lengthYards
                      )}
                      rules={Rules.NonNegativeNumber}
                      onChange={dataChange}
                      onFocus={handleFocus}
                      onBlur={handleVolumeCalculation}
                      disabled={isDisabled(
                        fieldsImperial.lineItems.lengthYards.isRawField,
                        dataView,
                        showRaw
                      )}
                      hidden={isHidden(
                        fieldsImperial.lineItems.lengthYards.isRawField,
                        dataView,
                        showRaw
                      )}
                      instrumented={true}
                    >
                      <Input
                        type="number"
                        min="0"
                        step="any"
                        placeholder={fieldsImperial.lineItems.lengthYards.label}
                        suffix="yd"
                      />
                    </FormItem>
                  </FormRowWithValidation>

                  <FormRowWithValidation>
                    <FormItem
                      label=" "
                      name={
                        namePrefix + fieldsImperial.lineItems.heightFeet.path
                      }
                      defaultValue={returnDefaultValueForLineItemField(
                        item.heightFeet,
                        lineItem.heightFeet
                      )}
                      rules={Rules.NonNegativeNumber}
                      onChange={dataChange}
                      onFocus={handleFocus}
                      onBlur={handleVolumeCalculation}
                      disabled={isDisabled(
                        fieldsImperial.lineItems.heightFeet.isRawField,
                        dataView,
                        showRaw
                      )}
                      hidden={isHidden(
                        fieldsImperial.lineItems.heightFeet.isRawField,
                        dataView,
                        showRaw
                      )}
                      instrumented={true}
                    >
                      <Input
                        type="number"
                        min="0"
                        step="any"
                        placeholder={fieldsImperial.lineItems.heightFeet.label}
                        suffix="ft"
                      />
                    </FormItem>

                    <FormItem
                      label=" "
                      name={
                        namePrefix + fieldsImperial.lineItems.widthFeet.path
                      }
                      defaultValue={returnDefaultValueForLineItemField(
                        item.widthFeet,
                        lineItem.widthFeet
                      )}
                      rules={Rules.NonNegativeNumber}
                      onChange={dataChange}
                      onFocus={handleFocus}
                      onBlur={handleVolumeCalculation}
                      disabled={isDisabled(
                        fieldsImperial.lineItems.widthFeet.isRawField,
                        dataView,
                        showRaw
                      )}
                      hidden={isHidden(
                        fieldsImperial.lineItems.widthFeet.isRawField,
                        dataView,
                        showRaw
                      )}
                      instrumented={true}
                    >
                      <Input
                        type="number"
                        min="0"
                        step="any"
                        placeholder={fieldsImperial.lineItems.widthFeet.label}
                        suffix="ft"
                      />
                    </FormItem>

                    <FormItem
                      label=" "
                      name={
                        namePrefix + fieldsImperial.lineItems.lengthFeet.path
                      }
                      defaultValue={returnDefaultValueForLineItemField(
                        item.lengthFeet,
                        lineItem.lengthFeet
                      )}
                      rules={Rules.NonNegativeNumber}
                      onChange={dataChange}
                      onFocus={handleFocus}
                      onBlur={handleVolumeCalculation}
                      disabled={isDisabled(
                        fieldsImperial.lineItems.lengthFeet.isRawField,
                        dataView,
                        showRaw
                      )}
                      hidden={isHidden(
                        fieldsImperial.lineItems.lengthFeet.isRawField,
                        dataView,
                        showRaw
                      )}
                      instrumented={true}
                    >
                      <Input
                        type="number"
                        min="0"
                        step="any"
                        placeholder={fieldsImperial.lineItems.lengthFeet.label}
                        suffix="ft"
                      />
                    </FormItem>
                  </FormRowWithValidation>

                  <FormRowWithValidation>
                    <FormItem
                      label=" "
                      name={
                        namePrefix + fieldsImperial.lineItems.heightInches.path
                      }
                      defaultValue={returnDefaultValueForLineItemField(
                        item.heightInches,
                        lineItem.heightInches
                      )}
                      rules={Rules.NonNegativeNumber}
                      onChange={dataChange}
                      onFocus={handleFocus}
                      onBlur={handleVolumeCalculation}
                      disabled={isDisabled(
                        fieldsImperial.lineItems.heightInches.isRawField,
                        dataView,
                        showRaw
                      )}
                      hidden={isHidden(
                        fieldsImperial.lineItems.heightInches.isRawField,
                        dataView,
                        showRaw
                      )}
                      instrumented={true}
                    >
                      <Input
                        type="number"
                        min="0"
                        step="any"
                        placeholder={
                          fieldsImperial.lineItems.heightInches.label
                        }
                        suffix="in"
                      />
                    </FormItem>

                    <FormItem
                      label=" "
                      name={
                        namePrefix + fieldsImperial.lineItems.widthInches.path
                      }
                      defaultValue={returnDefaultValueForLineItemField(
                        item.widthInches,
                        lineItem.widthInches
                      )}
                      rules={Rules.NonNegativeNumber}
                      onChange={dataChange}
                      onFocus={handleFocus}
                      onBlur={handleVolumeCalculation}
                      disabled={isDisabled(
                        fieldsImperial.lineItems.widthInches.isRawField,
                        dataView,
                        showRaw
                      )}
                      hidden={isHidden(
                        fieldsImperial.lineItems.widthInches.isRawField,
                        dataView,
                        showRaw
                      )}
                      instrumented={true}
                    >
                      <Input
                        type="number"
                        min="0"
                        step="any"
                        placeholder={fieldsImperial.lineItems.widthInches.label}
                        suffix="in"
                      />
                    </FormItem>

                    <FormItem
                      label=" "
                      name={
                        namePrefix + fieldsImperial.lineItems.lengthInches.path
                      }
                      defaultValue={returnDefaultValueForLineItemField(
                        item.lengthInches,
                        lineItem.lengthInches
                      )}
                      rules={Rules.NonNegativeNumber}
                      onChange={dataChange}
                      onFocus={handleFocus}
                      onBlur={handleVolumeCalculation}
                      disabled={isDisabled(
                        fieldsImperial.lineItems.lengthInches.isRawField,
                        dataView,
                        showRaw
                      )}
                      hidden={isHidden(
                        fieldsImperial.lineItems.lengthInches.isRawField,
                        dataView,
                        showRaw
                      )}
                      instrumented={true}
                    >
                      <Input
                        type="number"
                        min="0"
                        step="any"
                        placeholder={
                          fieldsImperial.lineItems.lengthInches.label
                        }
                        suffix="in"
                      />
                    </FormItem>
                  </FormRowWithValidation>
                </>
              )}

              {HasWeirdRebarWidth() && (
                <p className="validation-warning">
                  {REBAR_WIDTH_WARNING_MESSAGE}
                </p>
              )}
              {HasWeirdRebarLength() && (
                <p className="validation-warning">
                  {REBAR_LENGTH_WARNING_MESSAGE}
                </p>
              )}
            </details>
            <hr />
            <FormSpace />
            <FormRowWithValidation
              style={dataView === RAW ? { display: 'none' } : {}}
            >
              <div
                style={{
                  display: 'flex',
                  marginBottom: '0',
                  flexDirection: 'column',
                  flex: '1 1 0'
                }}
              >
                <div
                  className="ant-space ant-space-horizontal ant-space-align-center"
                  style={{ gap: '8px' }}
                >
                  <div className="ant-space-item"></div>
                  <div className="ant-space-item">
                    <span
                      className="ant-typography"
                      style={{
                        flex: '1 1 0px',
                        overflow: 'hidden',
                        opacity: '1'
                      }}
                    >
                      {!isImperial
                        ? fields.lineItems.calculatedVolume.label
                        : fieldsImperial.lineItems.calculatedVolume.label}
                    </span>
                  </div>
                </div>
                <Input
                  type="number"
                  name={namePrefix + fields.lineItems.calculatedVolume.path}
                  value={
                    lineItem && lineItem.calculatedVolume
                      ? _.round(lineItem.calculatedVolume, isImperial ? 3 : 5)
                      : 0
                  }
                  onFocus={handleVolumeCalculation}
                  onChange={dataChange}
                  suffix={!isImperial ? 'm3' : 'cu yd'}
                  min="0"
                  step="any"
                  disabled
                />
              </div>

              <div
                style={{
                  display: 'flex',
                  marginBottom: '0',
                  flexDirection: 'column',
                  flex: '1 1 0'
                }}
              >
                <div
                  className="ant-space ant-space-horizontal ant-space-align-center"
                  style={{ gap: '8px' }}
                >
                  <div className="ant-space-item"></div>
                  <div className="ant-space-item">
                    <span
                      className="ant-typography"
                      style={{
                        flex: '1 1 0px',
                        overflow: 'hidden',
                        opacity: '1'
                      }}
                    >
                      {!isImperial
                        ? fields.lineItems.estimatedWeight.label
                        : fieldsImperial.lineItems.estimatedWeight.label}
                    </span>
                  </div>
                </div>
                <Input
                  type="number"
                  name={namePrefix + fields.lineItems.estimatedWeight.path}
                  value={
                    lineItem && lineItem.estimatedWeight
                      ? _.round(lineItem.estimatedWeight, isImperial ? 3 : 5)
                      : 0
                  }
                  rules={Rules.NonNegativeNumber}
                  onChange={dataChange}
                  onFocus={handleWeightCalculation}
                  suffix={!isImperial ? 't' : 'lbs'}
                  min="0"
                  step="any"
                  disabled
                />
              </div>
            </FormRowWithValidation>

            <FormRowWithValidation>
              <FormItem
                label={fields.rawLineItems.volume.label}
                name={rawNamePrefix + fields.rawLineItems.volume.path}
                defaultValue={returnDefaultValueForLineItemField(
                  rawItem.volume,
                  lineItem['raw_volume']
                )}
                onChange={dataChange}
                onFocus={handleFocus}
                disabled={isDisabled(true, dataView, showRaw)}
                hidden={isHidden(true, dataView, showRaw)}
                instrumented={true}
              />

              <FormItem
                label={fields.rawLineItems.weight.label}
                name={rawNamePrefix + fields.rawLineItems.weight.path}
                defaultValue={returnDefaultValueForLineItemField(
                  rawItem.weight,
                  lineItem['raw_weight']
                )}
                onChange={dataChange}
                onFocus={handleFocus}
                disabled={isDisabled(true, dataView, showRaw)}
                hidden={isHidden(true, dataView, showRaw)}
                instrumented={true}
              />
            </FormRowWithValidation>

            <FormRowWithValidation>
              <FormItem
                label={
                  !isImperial
                    ? fields.lineItems.givenVolume.label
                    : fieldsImperial.lineItems.givenVolume.label
                }
                name={namePrefix + fields.lineItems.givenVolume.path}
                defaultValue={returnDefaultValueForLineItemField(
                  item.givenVolume,
                  lineItem.givenVolume
                )}
                rules={Rules.NonNegativeNumber}
                onChange={dataChange}
                onFocus={handleFocus}
                id={`deliveryLineItemVol_${index}`}
                simpleDataValidationRules={v =>
                  CheckVolume(
                    v,
                    lineItem.givenVolumeUoM,
                    lineItem.productGroups
                      ? lineItem.productGroups.split(':')[1]
                      : null
                  )
                }
                disabled={isDisabled(
                  fields.lineItems.givenVolume.isRawField,
                  dataView,
                  showRaw
                )}
                hidden={isHidden(
                  fields.lineItems.givenVolume.isRawField,
                  dataView,
                  showRaw
                )}
                instrumented={true}
              >
                <Input
                  type="number"
                  min="0"
                  step="any"
                  suffix={
                    <UnitSelector
                      name={
                        namePrefix +
                        fieldsImperial.lineItems.givenVolumeUoM.path
                      }
                      currentValue={lineItem.givenVolumeUoM}
                      allValues={
                        !isImperial
                          ? METRIC_VOLUME_UNITS
                          : IMPERIAL_VOLUME_UNITS
                      }
                      onChange={dataChange}
                      disabled={isDisabled(
                        fields.lineItems.givenVolume.isRawField,
                        dataView,
                        showRaw
                      )}
                    />
                  }
                />
              </FormItem>

              <FormItem
                label={
                  !isImperial
                    ? fields.lineItems.weight.label
                    : fieldsImperial.lineItems.weight.label
                }
                name={namePrefix + fields.lineItems.weight.path}
                defaultValue={returnDefaultValueForLineItemField(
                  item.weight,
                  lineItem.weight
                )}
                rules={Rules.NonNegativeNumber}
                onChange={dataChange}
                onFocus={handleFocus}
                id={`deliveryLineItemWeight_${index}`}
                simpleDataValidationRules={v =>
                  CheckWeight(
                    v,
                    lineItem.weightUoM,
                    lineItem.productGroups
                      ? lineItem.productGroups.split(':')[1]
                      : null
                  )
                }
                disabled={isDisabled(
                  fields.lineItems.weight.isRawField,
                  dataView,
                  showRaw
                )}
                hidden={isHidden(
                  fields.lineItems.weight.isRawField,
                  dataView,
                  showRaw
                )}
                instrumented={true}
              >
                <Input
                  type="number"
                  min="0"
                  step="any"
                  suffix={
                    <UnitSelector
                      name={
                        namePrefix + fieldsImperial.lineItems.weightUoM.path
                      }
                      currentValue={lineItem.weightUoM}
                      allValues={
                        !isImperial
                          ? METRIC_WEIGHT_UNITS
                          : IMPERIAL_WEIGHT_UNITS
                      }
                      onChange={dataChange}
                      disabled={isDisabled(
                        fields.lineItems.weight.isRawField,
                        dataView,
                        showRaw
                      )}
                    />
                  }
                />
              </FormItem>
            </FormRowWithValidation>

            <FormSpace />
            <hr />
            <details
              onClick={e => {
                // prevent default on the Click Event to stop it conflicting
                e.preventDefault()
              }}
              {...(concreteDatesOpen ? { open: true } : {})}
              style={{ position: 'relative' }}
            >
              <summary
                onClick={() => {
                  setConcreteDatesOpen(prev => !prev)
                }}
                style={{ width: 'fit-content' }}
              >
                <span>Concrete Produced &amp; Used Dates</span>
                {concreteDatesOpen && (
                  <UpCircleFilled title="Click to collapse" />
                )}
                {!concreteDatesOpen && (
                  <DownCircleOutlined title="Click to expand" />
                )}
              </summary>
              <div
                className={
                  showProducedAtDateWarning ? 'contains-validation-warning' : ''
                }
              >
                <FormRowWithValidation>
                  <FormItem
                    label={fields.rawLineItems.productionDate.label}
                    name={
                      rawNamePrefix + fields.rawLineItems.productionDate.path
                    }
                    defaultValue={returnDefaultValueForLineItemField(
                      rawItem.productionDate,
                      lineItem['raw_productionDate']
                    )}
                    onChange={dataChange}
                    onFocus={handleFocus}
                    disabled={isDisabled(true, dataView, showRaw)}
                    hidden={isHidden(true, dataView, showRaw)}
                    instrumented={true}
                  />

                  <FormItem
                    label={fields.rawLineItems.productionTime.label}
                    name={
                      rawNamePrefix + fields.rawLineItems.productionTime.path
                    }
                    defaultValue={returnDefaultValueForLineItemField(
                      rawItem.productionTime,
                      lineItem['raw_productionTime']
                    )}
                    onChange={dataChange}
                    onFocus={handleFocus}
                    disabled={isDisabled(true, dataView, showRaw)}
                    hidden={isHidden(true, dataView, showRaw)}
                    instrumented={true}
                  />
                </FormRowWithValidation>
                <FormItem
                  label={fields.lineItems.productionTime.label}
                  name={namePrefix + fields.lineItems.productionTime.path}
                  defaultValue={returnDefaultValueForLineItemField(
                    item.productionTime,
                    lineItem.productionTime
                  )}
                  onChange={dataChange}
                  onFocus={handleFocus}
                  id={`deliveryLineItemProducedAt_${index}`}
                  disabled={isDisabled(
                    fields.lineItems.productionTime.isRawField,
                    dataView,
                    showRaw
                  )}
                  hidden={isHidden(
                    fields.lineItems.productionTime.isRawField,
                    dataView,
                    showRaw
                  )}
                  instrumented={true}
                >
                  <Input
                    type="datetime-local"
                    max="9999-12-31T23:59"
                    style={isImperial ? { display: 'none' } : {}}
                  />
                </FormItem>
                {isImperial && (
                  <div style={dataView === RAW ? { display: 'none' } : {}}>
                    <div
                      style={{
                        marginTop: '-8px',
                        fontSize: '12px',
                        color: '#888888'
                      }}
                    >
                      (MM/DD/YYYY, hh:mm AM/PM)
                    </div>
                    <DateTimeUS
                      className="DateTimeUS"
                      id={
                        namePrefix +
                        fieldsImperial.lineItems.productionTimeUS.path
                      }
                      relatedFieldPath={
                        namePrefix + fields.lineItems.productionTime.path
                      }
                      handleDateBlur={handleUsProductionTimeBlur}
                      value={usDateFormatter(
                        lineItem.productionTime ? lineItem.productionTime : ''
                      )}
                      disabled={isDisabled(
                        fieldsImperial.lineItems.productionTimeUS.isRawField,
                        dataView,
                        showRaw
                      )}
                      hidden={isHidden(
                        fieldsImperial.lineItems.productionTimeUS.isRawField,
                        dataView,
                        showRaw
                      )}
                      instrumented={true}
                    />
                    <FormSpaceSmall />
                  </div>
                )}
                {showProducedAtDateWarning && (
                  <p className="validation-warning">
                    {producedAtDateWarningMessage}
                  </p>
                )}
              </div>

              <FormRowWithValidation>
                <FormItem
                  label={fields.rawLineItems.usedAtDate.label}
                  name={rawNamePrefix + fields.rawLineItems.usedAtDate.path}
                  defaultValue={returnDefaultValueForLineItemField(
                    rawItem.usedAtDate,
                    lineItem['raw_usedAtDate']
                  )}
                  onChange={dataChange}
                  onFocus={handleFocus}
                  disabled={isDisabled(true, dataView, showRaw)}
                  hidden={isHidden(true, dataView, showRaw)}
                  instrumented={true}
                />

                <FormItem
                  label={fields.rawLineItems.usedAtTime.label}
                  name={rawNamePrefix + fields.rawLineItems.usedAtTime.path}
                  defaultValue={returnDefaultValueForLineItemField(
                    rawItem.usedAtTime,
                    lineItem['raw_usedAtTime']
                  )}
                  onChange={dataChange}
                  onFocus={handleFocus}
                  disabled={isDisabled(true, dataView, showRaw)}
                  hidden={isHidden(true, dataView, showRaw)}
                  instrumented={true}
                />
              </FormRowWithValidation>
              <div
                className={
                  showUsedAtDateWarning ? 'contains-validation-warning' : ''
                }
                style={dataView === RAW ? { display: 'none' } : {}}
              >
                <FormItem
                  label={fields.lineItems.usedAtTime.label}
                  name={namePrefix + fields.lineItems.usedAtTime.path}
                  defaultValue={returnDefaultValueForLineItemField(
                    item.usedAtTime,
                    lineItem.usedAtTime
                  )}
                  onChange={dataChange}
                  onFocus={handleUsedAtTimeFocus}
                  id={`deliveryLineItemUsedAt_${index}`}
                  disabled={isDisabled(
                    fields.lineItems.usedAtTime.isRawField,
                    dataView,
                    showRaw
                  )}
                  hidden={isHidden(
                    fields.lineItems.usedAtTime.isRawField,
                    dataView,
                    showRaw
                  )}
                  instrumented={true}
                >
                  <Input
                    type="datetime-local"
                    max="9999-12-31T23:59"
                    style={isImperial ? { display: 'none' } : {}}
                  />
                </FormItem>
                {isImperial && (
                  <>
                    <div
                      style={{
                        marginTop: '-4px',
                        fontSize: '12px',
                        color: '#888888'
                      }}
                    >
                      (MM/DD/YYYY, hh:mm AM/PM)
                    </div>
                    <DateTimeUS
                      id={
                        namePrefix + fieldsImperial.lineItems.usedAtTimeUS.path
                      }
                      relatedFieldPath={
                        namePrefix + fields.lineItems.usedAtTime.path
                      }
                      handleDateBlur={handleUsUsedAtTimeBlur}
                      value={usDateFormatter(
                        lineItem.usedAtTime ? lineItem.usedAtTime : ''
                      )}
                      disabled={isDisabled(
                        fieldsImperial.lineItems.usedAtTimeUS.isRawField,
                        dataView,
                        showRaw
                      )}
                      hidden={isHidden(
                        fieldsImperial.lineItems.usedAtTimeUS.isRawField,
                        dataView,
                        showRaw
                      )}
                      instrumented={true}
                    />
                  </>
                )}
                {showUsedAtDateWarning && (
                  <>
                    <FormSpaceSmall />
                    <p className="validation-warning">
                      {usedAtDateWarningMessage}
                    </p>
                  </>
                )}
              </div>
              <FormSpaceSmall />
            </details>
            <hr />
            <FormSpace />

            <FormItem
              label={fields.rawLineItems.certifications.label}
              name={rawNamePrefix + fields.rawLineItems.certifications.path}
              defaultValue={
                rawItem.certifications && rawItem.certifications.length > 0
                  ? rawItem.certifications
                  : lineItem['raw_certifications']
              }
              onChange={dataChange}
              onFocus={handleFocus}
              disabled={isDisabled(true, dataView, showRaw)}
              hidden={isHidden(true, dataView, showRaw)}
              instrumented={true}
            />
            <FormItem
              label={fields.lineItems.givenCertifications.label}
              name={namePrefix + fields.lineItems.givenCertifications.path}
              defaultValue={
                item.givenCertifications && item.givenCertifications.length > 0
                  ? item.givenCertifications
                  : lineItem.givenCertifications
              }
              onChange={dataChange}
              onFocus={handleFocus}
              disabled={isDisabled(
                fields.lineItems.givenCertifications.isRawField,
                dataView,
                showRaw
              )}
              hidden={isHidden(
                fields.lineItems.givenCertifications.isRawField,
                dataView,
                showRaw
              )}
              instrumented={true}
            >
              <Select
                allowClear
                placeholder="Select certifications"
                style={{ width: '100%' }}
                onClear={() =>
                  handleOnSelect(
                    null,
                    namePrefix + fields.lineItems.givenCertifications.path
                  )
                }
                onSelect={value =>
                  handleOnSelect(
                    value,
                    namePrefix + fields.lineItems.givenCertifications.path
                  )
                }
                options={DeliveryProductCertOptions.map(item => ({
                  value: item,
                  label: item
                }))}
              />
            </FormItem>

            <ReferenceProductItemSelect
              namePrefix={namePrefix}
              lineItem={item}
              onChange={dataChange}
              dataView={dataView}
              showRaw={showRaw}
              isDisabled={isDisabled}
              isHidden={isHidden}
              isExtendedPGList={isExtendedPGList}
              productName={name}
            />
          </details>
        )}
      </StyledSection>
      <AddMultipleLineItemsModal
        moduleName={moduleName}
        isOpen={isDuplicateItemsMultipleOpen}
        onClose={closeDuplicateItemMultipleModal}
        onSubmit={val => {
          duplicateMultiple(val)
        }}
        duplicateLineItemName={item.name}
      />
    </FormProvider>
  )
}

export const UpCircleFilled = styled(Icons.UpCircleFilled)`
  font-size: 20px;
  color: ${colours.TEAL};
  position: absolute;
  top: 1px;
  right: 0;
`
export const DownCircleOutlined = styled(Icons.DownCircleOutlined)`
  font-size: 20px;
  color: ${colours.TEAL};
  position: absolute;
  top: 1px;
  right: 0;
`
export const UpCircleFilledInline = styled(Icons.UpCircleFilled)`
  font-size: 20px;
  color: ${colours.TEAL};
  margin-left: 0.25rem;
`
export const DownCircleOutlinedInline = styled(Icons.DownCircleOutlined)`
  font-size: 20px;
  color: ${colours.TEAL};
  margin-left: 0.25rem;
`
export const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 4px;
`
